/* eslint-disable jsx-a11y/anchor-is-valid */
import { useOnStateChanged, type StateDeclaration, UISref, type UIViewInjectedProps } from '@uirouter/react';
import { Layout as AntdLayout, Menu as AntdMenu } from 'antd';
import type React from 'react';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import type { IMenuItem } from '@/constants/menu';
import { authStore } from '@/stores';

const { Sider } = AntdLayout;
const { SubMenu } = AntdMenu;

interface MenuProps extends UIViewInjectedProps {
  collapsed: boolean;
  defaultSelectedKey?: string;
  menuItems: IMenuItem[];
}

export interface BaseMenuItemProps {
  state: string;
  icon: React.ReactElement;
  messageId: string;
  className?: string;
  style?: React.CSSProperties;
}

const BaseMenuItem = ({ state, icon, messageId, ...rest }: BaseMenuItemProps) => {
  return (
    <AntdMenu.Item icon={icon} {...rest}>
      <UISref to={state}>
        <a>
          <FormattedMessage id={messageId} />
        </a>
      </UISref>
    </AntdMenu.Item>
  );
};

const renderMenuItem = ({ key, state, messageId, permissions, icon }: IMenuItem) => {
  if (!authStore.hasPermissions(permissions)) {
    return null;
  }

  return <BaseMenuItem key={key} state={state} messageId={messageId} icon={icon} className="menu-modified" />;
};

const renderSubMenuItem = ({ key, messageId, permissions, icon, style }: IMenuItem, children: any) => {
  const additionalPermissions = children.map((item) => item.permissions);

  if (!authStore.hasAtLeastOnePermission([...permissions, ...additionalPermissions])) {
    return null;
  }

  return (
    <SubMenu key={key} title={<FormattedMessage id={messageId} />} icon={icon} className="sub-menu" style={style}>
      {children.map(renderMenuItem)}
    </SubMenu>
  );
};

const ADMIN_KEY = 'admin';

export const Menu = ({ collapsed, transition, menuItems }: MenuProps) => {
  // Derived from `menuItems` (source of truth: src/constants/menu.tsx).
  // Any item with parent === "admin" lives under the admin landing page, and its
  // route should highlight the admin item in the sidebar. Keeping this derived
  // (not a hardcoded list) avoids drift when admin children are added/renamed.
  const adminChildKeys = useMemo(() => menuItems.filter((m) => m.parent === ADMIN_KEY).map((m) => m.key), [menuItems]);

  const [routerState, setRouterState] = useState(transition.router.globals.current.name);
  const selectedKey = useMemo(() => {
    const segment = routerState.split('.')[1];
    return segment === ADMIN_KEY || adminChildKeys.includes(segment) ? ADMIN_KEY : segment;
  }, [routerState, adminChildKeys]);
  useOnStateChanged((state: StateDeclaration) => setRouterState(state.name));

  const rootItems = menuItems.filter((m) => m.parent === 'root');
  const adminItem = rootItems.find((m) => m.menuType === 'groupWithLanding');
  const itemsAboveAdmin = rootItems.filter((m) => m.menuType !== 'groupWithLanding');

  const renderItem = (menuItem: IMenuItem) => {
    if (menuItem.menuType && menuItem.menuType === 'group') {
      const subMenuItems = menuItems.filter((menu) => menu.parent === menuItem.key);
      return renderSubMenuItem(menuItem, subMenuItems);
    }
    return renderMenuItem(menuItem);
  };

  return (
    <Sider theme="light" collapsed={collapsed} width={220}>
      <div className="menu-sider-wrap">
        <div className="menu-sider-scroll">
          <AntdMenu theme="light" mode="inline" selectedKeys={[selectedKey]}>
            {itemsAboveAdmin.map((menuItem) => renderItem(menuItem))}
          </AntdMenu>
        </div>
        {adminItem && authStore.hasPermissions(adminItem.permissions) && (
          <div className="menu-admin-footer">
            <AntdMenu theme="light" mode="inline" selectedKeys={[selectedKey]}>
              <BaseMenuItem
                key={adminItem.key}
                state={adminItem.state}
                messageId={adminItem.messageId}
                icon={adminItem.icon}
                className="menu-modified"
              />
            </AntdMenu>
          </div>
        )}
      </div>
    </Sider>
  );
};
